/* API client: same-origin JSON with the CSRF header, typed errors, TanStack Query hooks. */
import { useQuery } from "@tanstack/react-query";
import type { LessonItem, MeView, PathResponse, PlansView } from "../shared/api";
import { isKey, type Key } from "./i18n";

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public data: Record<string, unknown> = {}) { super(message); }
}

export async function api<T>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const method = opts.method ?? (opts.body !== undefined ? "POST" : "GET");
  const r = await fetch(`/api/v1${path}`, {
    method,
    credentials: "same-origin",
    headers: { "x-lumi": "1", ...(opts.body !== undefined ? { "content-type": "application/json" } : {}) },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  const text = await r.text();
  const data = text ? (() => { try { return JSON.parse(text); } catch { return { message: text }; } })() : {};
  if (!r.ok) throw new ApiError(r.status, data.error ?? "server_error", data.message ?? "Error", data);
  return data as T;
}

export function errorKey(e: unknown): Key {
  if (e instanceof ApiError) {
    const k = `err.${e.code}`;
    if (isKey(k)) return k;
  }
  return "common.error";
}

export const qk = {
  me: ["me"] as const,
  path: (child: string) => ["path", child] as const,
  plans: ["plans"] as const,
  lessons: (child: string) => ["lessons", child] as const,
  report: (run: string) => ["report", run] as const,
};

export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: () => api<MeView>("/me"), staleTime: 15_000 });
}
export function usePath(child: string | undefined) {
  return useQuery({ queryKey: qk.path(child ?? ""), queryFn: () => api<PathResponse>(`/children/${child}/path`), enabled: !!child, staleTime: 10_000 });
}
export function usePlans(enabled = true) {
  return useQuery({ queryKey: qk.plans, queryFn: () => api<PlansView>("/billing/plans"), enabled });
}
export function useLessons(child: string | undefined) {
  return useQuery({ queryKey: qk.lessons(child ?? ""), queryFn: () => api<{ lessons: LessonItem[] }>(`/children/${child}/lessons`), enabled: !!child });
}

/** First-party telemetry (Appendix E): batched, sent with sendBeacon on page hide. */
const queue: { name: string; category: string; props?: Record<string, unknown> }[] = [];
const sessionId = Math.random().toString(36).slice(2, 10);
export function track(category: string, name: string, props?: Record<string, unknown>) {
  queue.push({ category, name, props });
  if (queue.length >= 20) flush();
}
function flush() {
  if (!queue.length) return;
  const body = JSON.stringify({ events: queue.splice(0, queue.length), session: sessionId });
  const blob = new Blob([body], { type: "application/json" });
  // sendBeacon cannot set the CSRF header, so telemetry goes through fetch keepalive.
  void fetch("/api/v1/telemetry", { method: "POST", keepalive: true, credentials: "same-origin", headers: { "content-type": "application/json", "x-lumi": "1" }, body: blob }).catch(() => undefined);
}
if (typeof window !== "undefined") {
  addEventListener("pagehide", flush);
  setInterval(flush, 30_000);
}
